/**
 * Cascada de ubigeo (departamento → provincia → distrito) sobre el catálogo
 * local `public/js/ubigeo-peru.json`.
 *
 * Centraliza la carga del catálogo y el reseteo en cascada para las pantallas
 * que filtran por zona (Pricing → Estadísticas, Pricing → Scraping…): al
 * cambiar de departamento se vacían provincia y distrito, y al cambiar de
 * provincia se vacía el distrito.
 *
 * Los valores que devuelve son los **ids del catálogo** (códigos de ubigeo);
 * para el nombre visible se busca en la lista correspondiente.
 */

import { useEffect, useState } from 'react';

import { cargarUbigeo, distritosDe, provinciasDe, type OpcionUbigeo } from '@/lib/ubigeo';

/** Contexto devuelto por `useUbigeo()`. */
export interface Ubigeo {
  departamentos: OpcionUbigeo[];
  provincias: OpcionUbigeo[];
  distritos: OpcionUbigeo[];
  departamentoId: string;
  provinciaId: string;
  distritoId: string;
  /** `true` mientras se carga el catálogo por primera vez. */
  cargando: boolean;
  /** Error de carga del catálogo, si lo hubo. */
  error: string | null;
  elegirDepartamento: (id: string) => void;
  elegirProvincia: (id: string) => void;
  elegirDistrito: (id: string) => void;
  limpiar: () => void;
}

/** Nombre visible de una opción del catálogo. */
export function nombreUbigeo(lista: OpcionUbigeo[], id: string): string {
  return lista.find((opcion) => opcion.id === id)?.name ?? '';
}

/** Id del catálogo para un nombre (`LIMA` → `15`), o `''`. */
export function idUbigeo(lista: OpcionUbigeo[], nombre: string): string {
  const buscado = String(nombre || '').trim().toUpperCase();

  return lista.find((opcion) => opcion.name.toUpperCase() === buscado)?.id ?? '';
}

export function useUbigeo(): Ubigeo {
  const [departamentos, setDepartamentos] = useState<OpcionUbigeo[]>([]);
  const [provincias, setProvincias] = useState<OpcionUbigeo[]>([]);
  const [distritos, setDistritos] = useState<OpcionUbigeo[]>([]);
  const [departamentoId, setDepartamentoId] = useState('');
  const [provinciaId, setProvinciaId] = useState('');
  const [distritoId, setDistritoId] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;

    cargarUbigeo()
      .then((datos) => {
        if (!vigente) return;

        setDepartamentos(datos.departamentos);
        setError(null);
      })
      .catch((fallo: unknown) => {
        if (!vigente) return;

        setError(
          fallo instanceof Error ? fallo.message : 'No se pudo cargar el catálogo de ubigeo.',
        );
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });

    return () => {
      vigente = false;
    };
  }, []);

  function elegirDepartamento(id: string) {
    setDepartamentoId(id);
    setProvinciaId('');
    setDistritoId('');
    setProvincias([]);
    setDistritos([]);

    if (!id) return;

    provinciasDe(id)
      .then(setProvincias)
      .catch(() => setProvincias([]));
  }

  function elegirProvincia(id: string) {
    setProvinciaId(id);
    setDistritoId('');
    setDistritos([]);

    if (!id) return;

    distritosDe(id)
      .then(setDistritos)
      .catch(() => setDistritos([]));
  }

  function limpiar() {
    setDepartamentoId('');
    setProvinciaId('');
    setDistritoId('');
    setProvincias([]);
    setDistritos([]);
  }

  return {
    departamentos,
    provincias,
    distritos,
    departamentoId,
    provinciaId,
    distritoId,
    cargando,
    error,
    elegirDepartamento,
    elegirProvincia,
    elegirDistrito: setDistritoId,
    limpiar,
  };
}
